/**
 * DMInboxButton — top-nav shortcut to /messages with an unread badge.
 * Polls /api/dm/unread-count every 30 seconds so the badge stays fresh
 * without needing websockets.
 */
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { MessageCircle } from 'lucide-react';
import { AuthContext } from '../App';

const DMInboxButton = () => {
  const { API, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    if (!user) return undefined;
    let alive = true;
    const load = async () => {
      try {
        const r = await axios.get(`${API}/dm/unread-count`);
        if (alive) setUnread(r.data.unread || 0);
      } catch { /* silent */ }
    };
    load();
    const iv = setInterval(load, 30000);
    return () => { alive = false; clearInterval(iv); };
  }, [API, user]);

  if (!user) return null;

  return (
    <button
      type="button"
      onClick={() => navigate('/messages')}
      className="relative inline-flex items-center justify-center h-9 w-9 rounded-full border border-border hover:bg-muted transition-colors flex-shrink-0"
      aria-label={unread ? `Direct messages — ${unread} unread` : 'Direct messages'}
      data-testid="dm-inbox-btn"
    >
      <MessageCircle className="w-4 h-4 text-foreground" />
      {unread > 0 && (
        <span
          className="absolute -top-1 -right-1 min-w-[16px] h-4 rounded-full bg-primary text-primary-foreground text-[9px] font-bold flex items-center justify-center px-1"
          data-testid="dm-unread-badge"
        >
          {unread > 9 ? '9+' : unread}
        </span>
      )}
    </button>
  );
};

export default DMInboxButton;
